import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Hammer, CheckCircle, ArrowRight } from "@phosphor-icons/react";

/* Shared "Coming soon" portal placeholder. Used in admin + doctor + patient portals
   to lock IA before features ship, without leaving the user staring at a blank screen. */
export default function PortalPlaceholder({
    title,
    eyebrow,
    description,
    bullets = [],
    primaryCta,
    primaryCtaTo,
    note,
    wave,
}) {
    return (
        <div data-testid={`portal-placeholder-${(title || "").toLowerCase().replace(/\s/g, "-")}`}>
            <div className="bg-card border border-border rounded-2xl p-8 sm:p-10">
                <div className="flex items-start justify-between gap-6 flex-wrap">
                    <div className="flex-1 min-w-0">
                        {eyebrow && <div className="overline text-primary mb-2">{eyebrow}</div>}
                        <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">{title}</h1>
                        {description && (
                            <p className="text-muted-foreground mt-3 max-w-2xl text-sm sm:text-base leading-relaxed">
                                {description}
                            </p>
                        )}
                    </div>
                    {wave && (
                        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-100 text-amber-800 text-[10px] font-bold tracking-[0.2em] uppercase shrink-0">
                            <Hammer size={12} weight="fill" /> {wave}
                        </div>
                    )}
                </div>

                {bullets.length > 0 && (
                    <div className="mt-7 pt-6 border-t border-border">
                        <div className="overline text-muted-foreground mb-3">What this page will show</div>
                        <ul className="space-y-2 max-w-2xl">
                            {bullets.map((b, i) => (
                                <li key={i} className="flex items-start gap-2.5 text-sm">
                                    <CheckCircle size={16} weight="fill" className="text-primary shrink-0 mt-0.5" />
                                    <span>{b}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {primaryCta && primaryCtaTo && (
                    <div className="mt-7">
                        <Link to={primaryCtaTo}>
                            <Button className="rounded-full">
                                {primaryCta} <ArrowRight size={14} className="ml-1.5" />
                            </Button>
                        </Link>
                    </div>
                )}

                {note && (
                    <p className="mt-6 text-xs text-muted-foreground italic max-w-2xl">{note}</p>
                )}
            </div>
        </div>
    );
}
